/** Espejo cliente de courseProfileValidation.js (bordes UI + rubricApi). */

export const VALID_EXAM_LEVELS = [
  'nt1',
  'nt2',
  '1basico',
  '2basico',
  '3basico',
  '4basico',
  '5basico',
  '6basico',
  '7basico',
  '8basico',
] as const;

export const VALID_SUBJECT_TAGS = [
  'matematica',
  'lenguaje',
  'musica',
  'ciencias',
  'historia',
  'ingles',
  'educacion_fisica',
  'general',
] as const;

export type ValidExamLevel = (typeof VALID_EXAM_LEVELS)[number];
export type ValidSubjectTag = (typeof VALID_SUBJECT_TAGS)[number];

export type CourseProfileField = 'level' | 'subjectTag' | 'subjectId';
export type CourseProfileIssueKind = 'invalid' | 'absent';
export type CourseProfileSource =
  | 'courseProfile'
  | 'linkedSessionContext'
  | 'subjectContext'
  | 'request';

export interface CourseProfileIssue {
  field: CourseProfileField;
  kind: CourseProfileIssueKind;
  received: unknown;
  source: CourseProfileSource;
}

export interface CourseProfileValidationResult {
  code: 'course_profile_invalid' | 'course_profile_incomplete' | 'ok';
  message: string;
  issues: CourseProfileIssue[];
  freeMode: boolean;
  blocked: boolean;
  level: ValidExamLevel | null;
  subjectTag: ValidSubjectTag | null;
}

const LEVEL_SET = new Set<string>(VALID_EXAM_LEVELS);
const SUBJECT_SET = new Set<string>(VALID_SUBJECT_TAGS);

function normalizeToken(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

/** Alias → tag canónico del catálogo Sala. */
export function canonicalizeSubjectTag(value: unknown): string {
  let normalized = String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (normalized === 'english' || normalized === 'ingles') normalized = 'ingles';
  if (normalized === 'matematicas') normalized = 'matematica';
  if (
    normalized === 'educacionfisica'
    || normalized === 'edfisica'
    || normalized === 'educacion_fisica'
    || normalized === 'ed.fisica'
    || normalized === 'educacion fisica'
  ) {
    normalized = 'educacion_fisica';
  }
  return normalized;
}

export function isValidExamLevel(value: unknown): value is ValidExamLevel {
  return LEVEL_SET.has(normalizeToken(value));
}

export function isValidSubjectTag(value: unknown): value is ValidSubjectTag {
  return SUBJECT_SET.has(canonicalizeSubjectTag(value));
}

export function validateCourseLevelAndSubject(params: {
  level?: unknown;
  subjectTag?: unknown;
  subjectId?: unknown;
  hasCourseContext?: boolean;
  source?: CourseProfileSource;
  /** Cliente: pasar true para exigir perfil completo cuando hay curso. */
  strictAbsent?: boolean;
}): CourseProfileValidationResult {
  const {
    level,
    subjectTag,
    subjectId,
    hasCourseContext = false,
    source = 'request',
    strictAbsent = false,
  } = params;

  const issues: CourseProfileIssue[] = [];
  const freeMode = !hasCourseContext;

  let resolvedLevel: ValidExamLevel | null = null;
  if (level == null || String(level).trim() === '') {
    issues.push({ field: 'level', kind: 'absent', received: level, source });
  } else if (!isValidExamLevel(level)) {
    issues.push({ field: 'level', kind: 'invalid', received: level, source });
  } else {
    resolvedLevel = normalizeToken(level) as ValidExamLevel;
  }

  const effectiveSubject = subjectTag ?? subjectId;
  let resolvedSubject: ValidSubjectTag | null = null;
  if (effectiveSubject == null || String(effectiveSubject).trim() === '') {
    issues.push({
      field: subjectTag != null && String(subjectTag).trim() !== '' ? 'subjectTag' : 'subjectId',
      kind: 'absent',
      received: effectiveSubject,
      source,
    });
  } else if (!isValidSubjectTag(effectiveSubject)) {
    issues.push({
      field: 'subjectTag',
      kind: 'invalid',
      received: effectiveSubject,
      source,
    });
  } else {
    resolvedSubject = canonicalizeSubjectTag(effectiveSubject) as ValidSubjectTag;
  }

  const hasInvalid = issues.some((issue) => issue.kind === 'invalid');
  const hasAbsentWithCourse = hasCourseContext && issues.some((issue) => issue.kind === 'absent');

  if (freeMode && !hasInvalid) {
    return {
      code: issues.length ? 'course_profile_incomplete' : 'ok',
      message: issues.length
        ? 'Modo libre: nivel/asignatura no fijados (sin defaults inventados).'
        : 'ok',
      issues,
      freeMode: true,
      blocked: false,
      level: resolvedLevel,
      subjectTag: resolvedSubject,
    };
  }

  const blocked = hasInvalid || (hasAbsentWithCourse && strictAbsent);
  const code = hasInvalid
    ? 'course_profile_invalid'
    : issues.length
      ? 'course_profile_incomplete'
      : 'ok';

  return {
    code,
    message: hasInvalid
      ? 'Nivel o asignatura del curso no son válidos. Revisa la configuración del curso antes de generar.'
      : 'Falta nivel o asignatura del curso. Completa el perfil del curso antes de generar.',
    issues,
    freeMode: false,
    blocked,
    level: resolvedLevel,
    subjectTag: resolvedSubject,
  };
}

export function mapCourseProfileValidationError(code: string): string {
  if (code === 'course_profile_invalid' || code === 'level inválido' || code === 'subjectTag inválido') {
    return 'Nivel o asignatura del curso no son válidos. Revisa la configuración del curso antes de generar.';
  }
  if (
    code === 'course_profile_incomplete' ||
    code === 'level requerido' ||
    code === 'subjectTag requerido'
  ) {
    return 'Falta nivel o asignatura del curso. Completa el perfil del curso antes de generar.';
  }
  return 'No se pudo validar el perfil del curso.';
}

/**
 * Espejo de requireMaterialLevelAndSubject (server) para bloquear UI de generación.
 */
export function requireMaterialLevelAndSubject(params: {
  level?: unknown;
  subjectTag?: unknown;
  subjectId?: unknown;
}): {
  ok: true;
  level: ValidExamLevel;
  subjectTag: ValidSubjectTag;
} | {
  ok: false;
  error: string;
  message: string;
  issues: CourseProfileIssue[];
} {
  const { level, subjectTag, subjectId } = params;
  const issues: CourseProfileIssue[] = [];

  let resolvedLevel: ValidExamLevel | null = null;
  if (level == null || String(level).trim() === '') {
    issues.push({ field: 'level', kind: 'absent', received: level, source: 'request' });
    return {
      ok: false,
      error: 'level requerido',
      message: 'Falta el nivel curricular. Configúralo en el curso antes de generar.',
      issues,
    };
  }
  if (!isValidExamLevel(level)) {
    issues.push({ field: 'level', kind: 'invalid', received: level, source: 'request' });
    return {
      ok: false,
      error: 'level inválido',
      message: `Nivel no reconocido: "${String(level)}".`,
      issues,
    };
  }
  resolvedLevel = normalizeToken(level) as ValidExamLevel;

  const effectiveSubject = subjectTag ?? subjectId;
  if (effectiveSubject == null || String(effectiveSubject).trim() === '') {
    issues.push({ field: 'subjectTag', kind: 'absent', received: effectiveSubject, source: 'request' });
    return {
      ok: false,
      error: 'subjectTag requerido',
      message: 'Falta la asignatura. Configúrala en el curso antes de generar.',
      issues,
    };
  }
  if (!isValidSubjectTag(effectiveSubject)) {
    issues.push({ field: 'subjectTag', kind: 'invalid', received: effectiveSubject, source: 'request' });
    return {
      ok: false,
      error: 'subjectTag inválido',
      message: `Asignatura no reconocida: "${String(effectiveSubject)}".`,
      issues,
    };
  }

  return {
    ok: true,
    level: resolvedLevel,
    subjectTag: normalizeToken(effectiveSubject) as ValidSubjectTag,
  };
}
